import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang, tr } from "../contexts/LangContext";
import { Anchor } from "lucide-react";

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export default function Register() {
  const { t } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", website: "" });
  const [err, setErr] = useState("");
  const [pendingMsg, setPendingMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);
  const tokenRef = useRef("");

  useEffect(() => {
    const renderWidget = () => {
      if (!widgetRef.current || widgetIdRef.current != null || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(widgetRef.current, {
        sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
        action: "register",
        "refresh-expired": "auto",
        callback: (token) => { tokenRef.current = token; },
        "expired-callback": () => { tokenRef.current = ""; },
        "error-callback": () => { tokenRef.current = ""; },
      });
    };
    let script;
    if (window.turnstile) {
      renderWidget();
    } else {
      script = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
      if (!script) {
        script = document.createElement("script");
        script.src = TURNSTILE_SRC;
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", renderWidget);
    }
    return () => {
      script?.removeEventListener("load", renderWidget);
      if (widgetIdRef.current != null) {
        try { window.turnstile?.remove(widgetIdRef.current); } catch (_) {}
        widgetIdRef.current = null;
      }
    };
  }, []);

  const resetWidget = () => {
    tokenRef.current = "";
    if (widgetIdRef.current != null) {
      try { window.turnstile?.reset(widgetIdRef.current); } catch (_) {}
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setPendingMsg("");
    const token = tokenRef.current || e.currentTarget.elements["cf-turnstile-response"]?.value || "";
    setLoading(true);
    const res = await register(form.email, form.password, form.name, { turnstile_token: token, website: form.website });
    setLoading(false);
    if (res.ok) {
      setPendingMsg(res.message || tr("Inscription enregistrée. Vérifiez votre boîte mail pour confirmer votre adresse."));
      setForm({ name: "", email: "", password: "", website: "" });
      resetWidget();
    } else {
      setErr(res.error);
      resetWidget();
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 pt-20" data-testid="register-page">
      <div className="flex items-center justify-center px-6 py-16 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="register-form">
          <div className="flex items-center gap-3 mb-4">
            <Anchor className="w-6 h-6 text-navy" strokeWidth={1.5} />
            <div className="font-serif text-xl text-navy">{tr("Club de Voile Royal")}</div>
          </div>
          <h1 className="font-serif text-4xl text-navy">{t.auth.registerTitle}</h1>
          <div className="divider-gold" />

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">{t.auth.name}</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border-b border-slate-300 focus:border-navy outline-none py-3 bg-transparent"
              data-testid="register-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">{t.auth.email}</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border-b border-slate-300 focus:border-navy outline-none py-3 bg-transparent"
              data-testid="register-email"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">{t.auth.password}</label>
            <input
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full border-b border-slate-300 focus:border-navy outline-none py-3 bg-transparent"
              data-testid="register-password"
            />
          </div>

          {err && <div className="text-sm text-red-600" data-testid="register-error">{err}</div>}
          {pendingMsg && (
            <div className="text-sm bg-club-yellow/15 border border-club-yellow text-navy p-4" data-testid="register-pending">
              <div className="font-semibold mb-1">{tr("Inscription envoyée")}</div>
              {pendingMsg}
            </div>
          )}

          <input
            type="text" name="website" value={form.website} tabIndex={-1} autoComplete="off" aria-hidden="true"
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, opacity: 0 }}
            data-testid="register-honeypot"
          />
          <div ref={widgetRef} data-testid="register-turnstile" />

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-navy text-white py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold transition-colors disabled:opacity-60"
            data-testid="register-submit"
          >
            {loading ? "..." : t.auth.submitRegister}
          </button>

          <div className="text-sm text-slate-600 text-center pt-4">
            {t.auth.hasAccount}{" "}
            <Link to="/login" className="text-navy underline decoration-gold underline-offset-4 hover:text-gold" data-testid="link-to-login">
              {t.auth.loginHere}
            </Link>
          </div>
        </form>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img
          src="https://images.unsplash.com/photo-1561728130-afd430af0493?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-navy/40" />
        <div className="absolute bottom-12 left-12 text-white max-w-md">
          <div className="label-eyebrow text-gold mb-3">{t.auth.registerTitle}</div>
          <p className="font-serif text-3xl leading-tight">{t.auth.registerSub}</p>
        </div>
      </div>
    </div>
  );
}
